import { Component, Input, OnDestroy, OnInit, Type } from '@angular/core';
import {
  ActionClass,
  ActionMode,
  ActionType,
  DynamicDialogConfig,
  DynamicDialogService,
  OnTableInit, SelectMode,
  TableConfig
} from '@kodit/core/shared-ui';
import { Subscription } from 'rxjs';
import { DialogSize } from '../../../../../shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import {
  EOtpremnicaTableDto,
  EOtpremniceClient,
  EPrijemnicaTableDto,
  GetEOtpremnicaTableQuery,
  GetEPrijemnicaTableQuery,
  IEPrijemnicaTableDto,
  PaginatedResultOfEOtpremnicaTableDto, RacunTableDto,
  StatusEOtpremnice,
  TipIzmenePosiljke, TipPristupa, TipRacuna,
  VrstaDokumentaOtpremnica,
  VrstaJoba,
  VrstaRacuna,
} from '@kodit/core/data-api';
import {
  AlertService,
  ConfigService,
  DocumentPreviewService,
  ensureFileUrl,
  LocationService,
  MixpanelService,
  SharedService,
} from '@kodit/core/services';
import { Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { EotpremnicaFormService } from '../otpremnice/eotpremnica-form.service';
import { selectFilterByKey } from '../../../../../shared-subforms/src/lib/racun/state/filters/filter.selectors';
import { PosaljiNaSefOtpremnicaFormComponent } from '../subforms/posalji-na-sef-otpremnica-form/posalji-na-sef-otpremnica-form.component';
import { StornoEotpremnicaFormComponent } from '../subforms/storno-eotpremnica-form/storno-eotpremnica-form.component';
import { StartVoznjeFormComponent } from '../subforms/start-voznje-form/start-voznje-form.component';
import { finalize } from 'rxjs/operators';
import { setFilter } from '../../../../../shared-subforms/src/lib/racun/state/filters/filter.actions';
import { SinhronizacijaEotpremniceFormComponent } from '../subforms/sinhronizacija-eotpremnice-form/sinhronizacija-eotpremnice-form.component';
import { ProgressService } from '../../../../../core-ui/src/lib/progress.service';

@Component({
  selector: 'kodit-eprijemnica-table',
  templateUrl: './eprijemnica-table.component.html',
  styleUrls: ['./eprijemnica-table.component.scss']
})
export class EprijemnicaTableComponent implements OnInit, OnDestroy, OnTableInit {

  /** Subs */
  private _subs: Subscription = new Subscription();

  /** Configurations */
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);

  /** Props */
  paginatedData!: PaginatedResultOfEOtpremnicaTableDto;
  filters!: GetEPrijemnicaTableQuery;
  private _skipFirst = 0;
  numberOfRowsDisplayed = 0;
  private _isBusy = false;

  isFirstLoad = true;
  isLoading = true;

  prijemniceTable: IEPrijemnicaTableDto[] = [];

  /** Configurations */
  tableConfig!: TableConfig;

  /** I/O */
  @Input() vrstaOtpremnice: VrstaDokumentaOtpremnica;

  constructor(
    private _locationService: LocationService,
    private _storage: Store,
    private _configService: ConfigService,
    private _client: EOtpremniceClient,
    private _alertService: AlertService,
    private _confirmationService: ConfirmationService,
    private _dialogService: DynamicDialogService,
    private _fs: EotpremnicaFormService,
    private _mixpanelService : MixpanelService,
    private _sharedService : SharedService,
    private _docPreviewService: DocumentPreviewService,
    private _progressService: ProgressService
  ) { }

  ngOnInit(): void {

    this.setTableConfig().then();

    this._subs.add(
      this._progressService
        .getJobZavrsen(VrstaJoba.SINHRONIZACIJA_ULAZNIH_EOTPREMNICA)
        .subscribe(() => {
          this._load();
        })
    );

    // reload tabele kada se filter promeni
    this._subs.add(
      this._storage
        .select(selectFilterByKey('EOtpremnice'))
        .subscribe((data) => {
          if (data) {
            this.filters = JSON.parse(data);
            this._skipFirst = this.filters.pageNumber ?? 0;
            this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
          } else {
            this.filters = new GetEPrijemnicaTableQuery({
              pageNumber: this._skipFirst,
              pageSize: this.numberOfRowsDisplayed,
            });
          }
          this._load();
        })
    );
  }

  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      isLazy: true,
      lazyCallbackFunction: (event) => {
        if (!this.isFirstLoad) {
          this._skipFirst = event.first!;
          this.numberOfRowsDisplayed = event.rows!;
        }
        this.updateFilterDataAndReload();
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      selectMode: this.vrstaOtpremnice == VrstaDokumentaOtpremnica.ULAZNA ? SelectMode.MULTI : SelectMode.SINGLE,
      tableHeader: 'Prijemnice',
      //onChipRemove: (chips: FilterChipDto) => this._handleChipRemove(chips),
      columns: [
        {
          field: 'brojPrijemnice',
          header: 'Broj',
          type: 'link',
          linkCallbackFunction: (_itemIndex: number) => {},
          columns: [
            {
              field: 'vezaFakture',
              subField: 'label',
              header: 'Veza',
              type: 'link',
              linkCallbackFunction: (itemIndex: number, _subIndex: number) =>
                this._goToRacunInfo(itemIndex),
            },
          ],
        },
        {
          field: 'stranka',
          header: 'Primalac robe',
          type: 'text',
        },
        {
          field: 'adresaOtpreme',
          header: 'Adresa otpreme',
          type: 'text',
        },
        {
          field: 'adresaOdredista',
          header: 'Adresa odredišta',
          type: 'text',
        },
        {
          field: 'datumOtpremnice',
          header: 'Datum otpremnice',
          type: 'text',
        },
        {
          field: 'datumFizickogPrijema',
          header: 'Datum fizičkog prijema',
          type: 'text',
        },
        {
          field: 'datumIzdavanja',
          header: 'Datum izdavanja',
          type: 'text',
        },
        {
          field: 'statusDto',
          subField: 'statusStr',
          header: 'Status',
          styleClass: 'pdv-status',
          styleClassField: 'statusBadgeStr',
          tooltipField: 'opis',
          type: 'badge',
        },
      ],
      headerActions: [
        {
          type: ActionType.CUSTOM,
          //tipPristupa: TipPristupa.PDV_CRUD,
          label: 'Sinhronizuj',
          icon: 'fa-light fa-rotate',
          hasAccessTooltip: 'Sinhronizuj sa sefom',
          noAccessTooltip: 'Nemate ovlašćenja za sinhronizaciju sa sefom',
          callback: () => {
            this.dialogConfig.header = `Sinhronizacija sa SEF`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fas fa-paper-plane',
              label: 'Sinhronizuj',
              class: 'mw-90',
            };
            this.openDialog(SinhronizacijaEotpremniceFormComponent);
          }
        },
        /* Konvertovanje vise otpremnica u izlaznu fakturu*/
        {
          type: ActionType.MULTI_CUSTOM,
          actionClass: ActionClass.OUTLINED,
          label: 'Konvertuj u fakturu',
          icon: 'fa-light fa-copy',
          hasAccessTooltip:
            'Selektujte otpremnice klikom na dugme ispred naziva prijemnice i konvertujte ih u izlaznu fakturu',
          isVisible: this.vrstaOtpremnice == VrstaDokumentaOtpremnica.ULAZNA,
          callback: (result: EPrijemnicaTableDto[]) => {
            if (
              !result.every(
                (val, i, arr) =>
                  val.strankaId ===
                  arr[0].strankaId
              )
            ) {
              this._alertService.addWarnMsg(
                'Akcija konvertovanja nije moguća. Prijemnice moraju imati istu stranku.'
              );
              return;
            }
            //Proveravamo da li je neka otpremnica vec konvertovana u fakturu
            if (
              !result.every(
                (prijemnicaTableDto) =>
                  !prijemnicaTableDto.jeVezana
              )
            ) {
              this._alertService.addWarnMsg(
                'Akcija konvertovanja nije moguća. Neka od selektovanih prijemnica je već konvertovana.'
              );
              return;
            }

            if (
              !result.every(
                (prijemnicaTableDto) =>
                  prijemnicaTableDto.statusDto?.status === StatusEOtpremnice.PRIHVACENO
              )
            ) {
              this._alertService.addWarnMsg(
                'Akcija konvertovanja nije moguća. Neka od selektovanih prijemnica nije prihvaćena.'
              );
              return;
            }
            this._locationService.routeToRacunCreateForEPrijemnica(
              result.map((otpremnica) => otpremnica.id)
            );
          },
        },
      ],
      rowActions: [
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-check',
          hasAccessTooltip: 'Prihvatite ulaznu prijemnicu',
          noAccessTooltip: 'Nemate ovlašćenja za prihvatanje ulazne prijemnice',
          callback: (index: number) => {
            this._confirmationService.confirm({
              header: 'Potvrda prihvatanja',
              message: 'Ovim potvrdjujete prihvatanje prijemnice.',
              acceptLabel: 'Prihvati',
              rejectLabel: 'Odustani',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this._akcijaPrijemnice(this._getPaginatedItem(index).id!, TipIzmenePosiljke.PRIHVACENA_PRIJEMNICA);
              },
            });
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.PRIMLJENO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.ULAZNA
            );
          },
        },
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-x',
          hasAccessTooltip: 'Odbijte prijemnicu',
          noAccessTooltip: 'Nemate ovlašćenja za odbijanje prijemnice',
          callback: (index: number) => {
            this._confirmationService.confirm({
              header: 'Potvrda odbijanja',
              message: 'Ovim potvrdjujete odbijanje prijemnice.',
              acceptLabel: 'Odbij',
              rejectLabel: 'Odustani',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                this._akcijaPrijemnice(this._getPaginatedItem(index).id!, TipIzmenePosiljke.ODBIJENA_PRIJEMNICA);
              },
            });
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.PRIMLJENO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.ULAZNA
            );
          },
        },
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          icon: 'fas fa-print',
          hasAccessTooltip: 'Odštampajte prijemnicu u PDF formatu',
          noAccessTooltip: 'Nemate ovlašćenja za štampanje prijemnice',
          callback: (index: number) => {
            this._openDocumentPreview(
              this._getPaginatedItem(index).id,
              this._getPaginatedItem(index).brojPrijemnice
            );
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status !== StatusEOtpremnice.SLANJE
            );
          },
        },
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fas fa-exchange-alt',
          label: 'Konvertuj u fakturu',
          hasAccessTooltip: 'Konvertujte otpremnicu u izlaznu fakturu',
          callback: (index: number) => {
            this._locationService.routeToRacunCreateForEPrijemnica([
              this._getPaginatedItem(index).id,
            ]);
          },
          shouldDisplayByCondition: (rowData: EPrijemnicaTableDto) => {
            return (
              rowData.statusDto?.status === StatusEOtpremnice.PRIHVACENO
              && !rowData.jeVezana
            );
          },
        },

      ],
      // advancedFilter: {
      //   component: PdvEvidencijaPojedinacna39FilterComponent,
      //   data: new GetPdvEvidencijaPojedinacna39TableQuery(),
      // },
    });
  }

  private _load() {
    if (this._isBusy) {
      return;
    }

    this._configService.setIsBusy = true;
    this._isBusy = true;

    if (!this.filters) {
      this.filters = new GetEOtpremnicaTableQuery({
        pageNumber: this._skipFirst,
        pageSize: this.numberOfRowsDisplayed,
      });
    }
    this.filters.vrstaDokumenta = this.vrstaOtpremnice;

    this.isLoading = true;
    this._subs.add(
      this._client
        .getForTableEPrijemnica(this.filters as GetEPrijemnicaTableQuery)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this._isBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe(
          (res) => {
            this.paginatedData = res;
            this.prijemniceTable = res.data!;
            this.prijemniceTable?.forEach((item) => {
              (item as any).vezaFakture = item.racunBroj
                ? [{ label: item.racunBroj }]
                : [];
            });
            //this.chipItems = res.activeFilters!;
            this.tableConfig.advancedFilter!.data = this.filters;
          },
          (error) => {
            this._configService.setIsBusy = false;
          }
        )
    );
  }

  private updateFilterDataAndReload() {
    if (
      (this._skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this._skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      this._updateStorage();
    }
  }

  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: 'EOtpremnice',
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _routeToCreate() {
    this._locationService.routeToEOtpremnicaCrete().then();
  }

  private _routeToEdit(rowIndex: number) {
    this._locationService
      .routeToEOtpremnicaUpdate(
        this._getPaginatedItem(rowIndex).id!
      )
      .then();
  }

  private _goToRacunInfo(rowIndex: number) {
    const item = this._getPaginatedItem(rowIndex);
    if (!item.racunId || !item.racunTip) {
      return;
    }
    let vrsta: VrstaRacuna;
    if (item.racunTip === TipRacuna.IZLAZNA_FAKTURA) {
      vrsta = VrstaRacuna.IZLAZNI;
    } else if (item.racunTip === TipRacuna.ULAZNA_FAKTURA) {
      vrsta = VrstaRacuna.ULAZNI;
    } else {
      vrsta = item.racunVrsta;
    }
    if (!vrsta) {
      return;
    }
    this._locationService
      .routeToRacunKartica(item.racunId, item.racunTip, vrsta)
      .then();
  }

  private _getPaginatedIndex(index: number): number {
    return index - this._skipFirst;
  }

  private _getPaginatedItem(index: number): IEPrijemnicaTableDto {
    return this.prijemniceTable[this._getPaginatedIndex(index)];
  }

  private _akcijaPrijemnice(id: number, tipIzmene: TipIzmenePosiljke) {
    this._sharedService.displayLoadingScreen(true, "Prihvatanje/odbijanje u toku");
    this._subs.add(
      this._client
        .akcijaOtpremnice(
          id,
          tipIzmene == TipIzmenePosiljke.PRIHVACENA_PRIJEMNICA
              ? TipIzmenePosiljke.PRIHVACENA_PRIJEMNICA
              : TipIzmenePosiljke.ODBIJENA_PRIJEMNICA,
          null
        )
        .pipe(
          finalize(() => {
            this._sharedService.resetLoadingScreen();
          })
        )
        .subscribe((res) => {
          if(res.succeeded){
            this._mixpanelService.izlaznaFakturaSentToSefSucceeded();
            this._alertService.addSuccessMsg(res.data);
            this._load();
            return;
          }
          this._alertService.addFailedMsg(res.messages[0]);
        })
    );
  }

  openDialog(
    formType: Type<PosaljiNaSefOtpremnicaFormComponent>
      | Type<StornoEotpremnicaFormComponent>
      | Type<StartVoznjeFormComponent>
      | Type<SinhronizacijaEotpremniceFormComponent>
  ): void {
    const ref = this._dialogService.open(formType, this.dialogConfig);

    this._subs.add(
      ref.onClose.subscribe((dto: any) => {
        if (dto) {
          this._load();
        }
        this.dialogConfig.hideFooter = false;
      })
    );
  }

  private _openDocumentPreview(
    otpremnicaId: number,
    fileName: string
  ) {
    this._subs.add(
      this._docPreviewService
        .openDialog(
          `eotpremnice/get-pdf/${otpremnicaId}/${ensureFileUrl(fileName)}`
        )
        .onClose.subscribe((res: any) => {
          // just reset dialog to default
          this.dialogConfig.maximisable = true;
          this.dialogConfig.maximized = false;
          this.dialogConfig.hideFooter = false;
        })
    );
  }


  ngOnDestroy() {
    this._subs.unsubscribe();
  }

}
